import {
  DEFAULT_PRICE_DECIMALS,
  HANDICAP,
  DEFAULT,
  FRACTION_PICKS,
  AMERICAN,
  DECIMAL,
  FRACTION,
  HONG_KONG,
  INDONESIAN,
  MALAYSIAN,
  ITALIAN,
  PERCENTAGE,
  WINNINGS,
  TOTAL_PERCENT,
  MALAYSIAN_PRICE_DECIMALS,
  oddTypeSP,
  OVER_PARTID,
  UNDER_PARTID,
  TOTALS,
} from 'constants/oddsLines'

import { last, omit, orderBy, groupBy } from 'lodash'
import Fraction from 'fraction.js'
import { getLineText } from '@advisiondevelopment/data-formatter'

import { groupArrayOfObjectsByKey } from './arrays'

const DRAW = 'DRAW'

function convertDecimalAdjustToFraction(decimalAdjustValue) {
  const [number, decimals] = `${decimalAdjustValue}`
    .split('.')
    .map((value) => Number(value, DEFAULT_PRICE_DECIMALS))
  const numberToDisplay = number === 0 ? '' : Math.abs(number)

  switch (decimals) {
    case 25:
      return `${numberToDisplay}¼`
    case 5:
      return `${numberToDisplay}½`
    case 75:
      return `${numberToDisplay}¾`
    case 0:
    default:
      return Math.abs(decimalAdjustValue)
  }
}

export function getSpreadValue(adjValue, spreadType, sideId) {
  const valueSign = adjValue > 0 ? '+' : '-'
  const adjustValue = convertDecimalAdjustToFraction(adjValue)

  switch (spreadType) {
    case HANDICAP:
      if (!adjustValue) return 'PK'

      return `${valueSign}${adjustValue}`
    case FRACTION_PICKS:
      if (!adjustValue) return 'PK'

      return `${valueSign}${convertDecimalAdjustToFraction(adjustValue)}`
    case TOTALS:
      if (!adjustValue) return ''

      if (sideId === OVER_PARTID) {
        return `o${adjustValue}`
      }

      if (sideId === UNDER_PARTID) {
        return `u${adjustValue}`
      }
      break
    case DEFAULT:
    default:
      return ''
  }

  return ''
}

function convertDecimalToAmerican(price) {
  if (price === 1) return TOTAL_PERCENT

  return Math.round(
    price >= 2.0 ? (price - 1) * TOTAL_PERCENT : -TOTAL_PERCENT / (price - 1),
  )
}

function convertToAmerican(price) {
  const americanPrice = price.toFixed().replace(/[.,]00$/, '')

  return Number(americanPrice)
}

function _getFractionText(fraction) {
  const { numerator, denominator } = fraction

  return `${numerator}/${denominator}`
}

function convertToHongKong(price) {
  return price > 0 ? price / TOTAL_PERCENT : (TOTAL_PERCENT / price) * -1
}

function convertToIndonesian(price) {
  return price / TOTAL_PERCENT
}

function convertToItalian(price) {
  const hongKongPrice = convertToHongKong(price)

  return hongKongPrice * TOTAL_PERCENT
}

function convertToPercentage(price) {
  return (1 / price) * TOTAL_PERCENT
}

function convertToMalay(price) {
  return price <= 2 ? price - 1 : 1 / (1 - price)
}

function convertToWinning(price, winningStake) {
  return parseFloat(convertToHongKong(price)) * parseFloat(winningStake)
}

const isSPOddType = (decimalOddValue) => oddTypeSP.VALUE === decimalOddValue

function getDisplayDecimals(oddsFormat) {
  switch (oddsFormat) {
    case WINNINGS:
    case AMERICAN:
      return 0
    case MALAYSIAN:
      return MALAYSIAN_PRICE_DECIMALS
    default:
      return DEFAULT_PRICE_DECIMALS
  }
}

function convertToFraction(pri, ap) {
  const americanPrice = ap || convertDecimalToAmerican(pri)
  const fraction = new Fraction(americanPrice / TOTAL_PERCENT)

  return {
    numerator: americanPrice > 0 ? fraction.n : fraction.d,
    denominator: americanPrice > 0 ? fraction.d : fraction.n,
  }
}

function _parseOdd(args) {
  const { oddsFormat, price, winningStake, american } = args
  const americanPrice =
    Number(american) || convertDecimalToAmerican(Number(price))

  switch (oddsFormat) {
    case AMERICAN:
      return convertToAmerican(americanPrice)
    case DECIMAL:
      return price
    case FRACTION:
      return _getFractionText(convertToFraction(price, americanPrice))
    case HONG_KONG:
      return convertToHongKong(americanPrice)
    case INDONESIAN:
      return convertToIndonesian(americanPrice)
    case MALAYSIAN:
      return convertToMalay(price)
    case ITALIAN:
      return convertToItalian(americanPrice)
    case PERCENTAGE:
      return convertToPercentage(price)
    case WINNINGS:
      return convertToWinning(americanPrice, winningStake)
    default:
      return price
  }
}

function getOddPrefix(oddsFormat, price) {
  if (price > 0 && (oddsFormat === AMERICAN || oddsFormat === INDONESIAN))
    return '+'

  return ''
}

function getOddSuffix(oddsFormat) {
  if (oddsFormat === PERCENTAGE) return '%'

  return ''
}

function getOddDisplayText(args) {
  const {
    oddsFormat,
    winningStake,
    numerator,
    denominator,
    decimal,
    american,
  } = args

  if (isSPOddType(decimal)) return oddTypeSP.LABEL

  if (numerator && denominator && oddsFormat === FRACTION)
    return `${numerator}/${denominator}`

  const decimals = getDisplayDecimals(oddsFormat)

  let parsedOdd

  parsedOdd = _parseOdd({
    oddsFormat,
    price: parseFloat(decimal || 0),
    winningStake,
    american,
  })

  if (oddsFormat !== FRACTION && oddsFormat !== AMERICAN) {
    parsedOdd = parsedOdd.toFixed(decimals)
  }

  return `${getOddPrefix(oddsFormat, parsedOdd)}${parsedOdd}${getOddSuffix(oddsFormat)}`
}

export const getOddWithSpreadDisplayText = (
  line = {},
  winningStake,
  oddsFormat,
  marketFormat,
) => {
  const { adj, fpn, fpd, pri, ap, realpartid, partid } = line
  const price =
    pri &&
    getOddDisplayText({
      oddsFormat,
      winningStake,
      numerator: fpn,
      denominator: fpd,
      decimal: pri,
      american: ap,
    })
  const spreadValue = getSpreadValue(adj, marketFormat, realpartid || partid)
  const spreadDisplay = spreadValue ? `${spreadValue} ` : ''

  return price && `${spreadDisplay}${price}`
}

const _orderLinesBy = (lines, criterion, priority) =>
  last(orderBy(lines, criterion, priority)) || {}

const _setByHandicap = (lines) => {
  const cleanLines = lines.filter(({ iof }) => !iof)
  const byParticipant = omit(groupBy(cleanLines, 'partid'), 'null')

  return Object.values(byParticipant).reduce((result, linesByParticipant) => {
    const { adj, pri } = _orderLinesBy(
      linesByParticipant,
      ['adj', 'pri'],
      ['asc', 'asc'],
    )

    const newLines = linesByParticipant.map((line) => ({
      ...line,
      isBestOdd: line.adj === adj && line.pri === pri,
    }))

    return [...result, ...newLines]
  }, [])
}

const _setByTotals = (lines) => {
  const cleanLines = lines.filter(({ iof }) => !iof)
  const byParticipant = omit(groupBy(cleanLines, 'partid'), 'null')
  const { adj: adjOver, pri: priOver } = _orderLinesBy(
    byParticipant[OVER_PARTID],
    ['adj', 'pri'],
    ['desc', 'asc'],
  )
  const { adj: adjUnder, pri: priUnder } = _orderLinesBy(
    byParticipant[UNDER_PARTID],
    ['adj', 'pri'],
    ['asc', 'asc'],
  )

  return lines.map((line) => ({
    ...line,
    isBestOdd:
      line.partid === OVER_PARTID
        ? line.adj === adjOver && line.pri === priOver
        : line.adj === adjUnder && line.pri === priUnder,
  }))
}

const _setByDefault = (lines) => {
  const cleanLines = lines.filter(({ iof }) => !iof)
  const byParticipant = omit(groupBy(cleanLines, 'boid'), 'null')

  return Object.values(byParticipant).reduce((result, linesByBettingOption) => {
    const { adj, pri } = _orderLinesBy(linesByBettingOption, 'pri', 'asc')

    const newLines = linesByBettingOption.map((line) => ({
      ...line,
      isBestOdd: line.adj === adj && line.pri === pri,
    }))

    return [...result, ...newLines]
  }, [])
}

export function getLinesWithBestOdds(lines, spreadType) {
  if (!Array.isArray(lines)) return []

  switch (spreadType) {
    case HANDICAP:
    case FRACTION:
      return _setByHandicap(lines)
    case TOTALS:
      return _setByTotals(lines)
    case DRAW:
    case DEFAULT:
    default:
      return _setByDefault(lines)
  }
}

const _getFormatByMtid = (eventMarkets) =>
  eventMarkets.reduce((acc, { mtid, format }) => {
    acc[mtid] = format

    return acc
  }, {})

export const formatBestLines = (lines, eventMarkets, format) => {
  const marketFormats = _getFormatByMtid(eventMarkets)

  return lines
    .map((line) => ({
      ...getLineText({
        oddsFormat: format,
        skipOU: line.skipOU,
        marketFormat: marketFormats[line.mtid],
        line: { ...line, partid: line.realPartid || line.partid },
      }),
      ...line,
    }))
    .sort((bestLineA, bestLineB) => bestLineA.pri - bestLineB.pri)
}

export const formatLines = (lines, eventMarkets, format) => {
  const marketFormats = _getFormatByMtid(eventMarkets)
  const linesWithDisplay = lines.map((line) => {
    const newLine = {
      ...line,
      partid: line.realPartid ? line.realPartid : line.partid,
    }

    return {
      ...getLineText({
        line: newLine,
        oddsFormat: format,
        skipOU: line.skipOU,
        marketFormat: marketFormats[line.mtid],
      }),
      ...line,
    }
  })
  const linesByPartid = groupBy(linesWithDisplay, 'partid')

  // At this point we have an object of lines grouped by partid: { <partid>: [<line>, ...]}
  // Here we are going to group the array of lines for each partid into paid groups: { <partid>: { <paid>: <line> }}
  return Object.keys(linesByPartid).reduce((acc, partid) => {
    const lineList = linesByPartid[partid]

    acc[partid] = groupArrayOfObjectsByKey(lineList, 'paid')

    return acc
  }, {})
}

const _normalizeLinesByParticipants = (participants = [], consensus = []) => {
  const partIds = participants.map(({ partid }) => partid)

  return partIds.reduce((acc, curr) => {
    const lines = consensus.filter((line) => line.partid === curr)

    if (lines.length) {
      acc.push(...lines)
    } else {
      acc.push({ partid: curr, perc: 0, wag: 0 })
    }

    return acc
  }, [])
}

const _setWager = (participants = [], consensus = []) => {
  const lines = _normalizeLinesByParticipants(participants, consensus)
  const useDashAsDisplay = lines.every((line) => (line.perc || 0) === 0)

  return lines.map((line) => ({
    ...line,
    wager: {
      value: line.wag,
      display: useDashAsDisplay ? '-' : `${line.perc}%`,
    },
  }))
}

export const formatConsensus = (participants = [], consensus = []) =>
  _setWager(participants, consensus)
